import { mapGetters } from "vuex";

export const addNewChatAndCallMixin = {
  computed: {
    ...mapGetters({
      getAppointmentType: "getAppointmentType", // Get the appointment type details
      selectedProviderId: "getSelectedProviderID", // Get the selected provider id
      userDetails: "getUserDetails", // User details like role, id etc
      searchResults: "getSearchResults", // For search result
      userRole: "getUserRole"
    }),
    ...mapGetters({
      searchBy: "getSearchValue" // Search value
    }),
    ...mapGetters("favourites", {
      favouriteMode: "getFavouriteMode" // Weather in favourtite mode or not
    }),
    /**
     * All the clients
     */
    clients() {
      // Step 1: Base case
      if (
        !this.selectedProviderId ||
        !this.getAppointmentType ||
        typeof this.getAppointmentType !== "object" ||
        !(this.selectedProviderId in this.getAppointmentType.clients)
      ) {
        return false;
      }

      // Step 2: Map and filter
      return this.getAppointmentType.clients[this.selectedProviderId]
        .map(val => {
          return {
            ...val,
            name: val.name.replace(/&amp;/gi, "&")
          };
        })
        .filter(val => {
          if (this.favouriteMode) {
            return (
              val.name
                .toLowerCase()
                .search(this.escape(this.searchBy.toLowerCase())) !== -1 &&
              val.favourite_id !== 0
            );
          }
          return (
            val.name
              .toLowerCase()
              .search(this.escape(this.searchBy.toLowerCase())) !== -1
          );
        });
    },
    /**
     * List of all the filtered clients for chat (who have user_id)
     */
    filteredClients() {
      return this.clients ? this.clients.filter(this.checkIfUserHasId) : false;
    },
    /**
     * All the people
     */
    people() {
      if (this.selectedProviderId) {
        if (this.selectedProviderId in this.getAppointmentType.people) {
          return this.getAppointmentType.people[this.selectedProviderId].map(
            person => {
              person.name = person.name.replace(/&amp;/gi, "&");
              return person;
            }
          );
        }
      }
      return false;
    },
    /**
     * Filtered people with user id for chat
     */
    filteredPeopleForChat() {
      return this.people
        ? this.people.filter(this.checkIfUserHasId).filter(this.checkNameAndFav)
        : false;
    },
    /**
     * All the events
     */
    events() {
      if (this.selectedProviderId) {
        if (this.selectedProviderId in this.getAppointmentType.events) {
          return this.getAppointmentType.events[this.selectedProviderId].map(
            event => {
              {
                event.name = event.name.replace(/&amp;/gi, "&");
                return event;
              }
            }
          );
        }
      }
      return false;
    },
    /**
     * All the groups
     */
    groups() {
      if (this.selectedProviderId) {
        if (this.selectedProviderId in this.getAppointmentType.groups) {
          return this.getAppointmentType.groups[this.selectedProviderId].map(
            group => {
              group.name = group.name.replace(/&amp;/gi, "&");
              return group;
            }
          );
        }
      }
      return false;
    },
    /**
     * Filterd group for chat
     */
    filteredGroupForChat() {
      return this.groups && this.userRole !== 24
        ? this.groups
            .filter(this.checkNameAndFav)
            .filter(this.individualGroupCheck)
        : false;
    },
    /**
     * All the app users
     */
    appUsers() {
      if (this.selectedProviderId) {
        if (this.selectedProviderId in this.getAppointmentType.appUsers) {
          return this.getAppointmentType.appUsers[
            this.selectedProviderId
          ].filter(user => {
            return this.userDetails.id !== user.id;
          });
        }
      }
      return false;
    },
    /**
     * All the subscribers
     */
    subscribers() {
      if (this.getAppointmentType.subscribers.length > 0) {
        return this.getAppointmentType.subscribers.filter(user => {
          return this.userDetails.id !== user.id;
        });
      } else {
        return false;
      }
    },
    /**
     * Filtered users for chat
     */
    filteredUsersForChat() {
      // Step 1: Create base users
      let users = [];
      if (this.appUsers) {
        users = [...this.appUsers];
      }
      if (this.subscribers) {
        users = [...users, ...this.subscribers];
      }

      // Step 2: Sort
      users = users.sort(this.sortNames);

      // Step 3: Return users
      return users.length > 0
        ? users.filter(this.checkIfUserHasId).filter(this.checkNameAndFav)
        : false;
    },
    /**
     * All individual others
     */
    othersAll() {
      let others = [];
      if (this.people) {
        others = [...this.people];
      }
      if (this.subscribers) {
        others = [...others, ...this.subscribers];
      }
      if (this.appUsers) {
        others = [...others, ...this.appUsers];
      }
      return others.sort(this.sortNames).filter(val => {
        if (this.favouriteMode) {
          return (
            val.name
              .toLowerCase()
              .search(this.escape(this.searchBy.toLowerCase())) !== -1 &&
            val.favourite_id !== 0
          );
        }
        return (
          val.name
            .toLowerCase()
            .search(this.escape(this.searchBy.toLowerCase())) !== -1
        );
      });
    },
    /**
     * Others including groups
     */
    othersAlternate() {
      let others = [];
      if (this.people) {
        others = [...this.people];
      }
      if (this.events) {
        others = [...others, ...this.events];
      }
      if (this.groups) {
        others = [...others, ...this.groups];
      }
      return others.sort(this.sortNames).filter(val => {
        if (this.favouriteMode) {
          return (
            val.name
              .toLowerCase()
              .search(this.escape(this.searchBy.toLowerCase())) !== -1 &&
            val.favourite_id !== 0
          );
        }
        return (
          val.name
            .toLowerCase()
            .search(this.escape(this.searchBy.toLowerCase())) !== -1
        );
      });
    },
    /**
     * Others list for chat
     */
    others() {
      // Step 1: Check if others all is empty
      if (this.othersAlternate.length <= 0) {
        return false;
      }

      // Step 2: When others all is not empty
      return this.othersAlternate.filter(other => {
        return (
          this.checkIfUserHasId(other) || other.type === "group_appointment"
        );
      });
    },
    /**
     * List for phone
     */
    allPhone() {
      // Step 1: Create a default list
      let allUsers = [];

      // Step 2: Add whichever list is there to the list
      if (this.clients) {
        allUsers = [...this.clients];
      }
      if (this.othersAll) {
        allUsers = [...allUsers, ...this.othersAll];
      }

      // Step 3: Sort and return the list
      return allUsers.sort(this.sortNames);
    },
    /**
     * Clients for search result
     */
    allSearchClients() {
      // Step 1: Create a default list
      let allUsers = [];

      // Step 2: Add whichever list is there to the list
      if (this.clients) {
        allUsers = [...this.clients];
      }

      // Step 3: Sort and return the list
      return allUsers.sort(this.sortNames);
    },
    /**
     * Others for search result
     */
    allSearchOthers() {
      // Step 1: Create a default list
      let allUsers = [];

      // Step 2: Add whichever list is there to the list
      if (this.othersAlternate) {
        allUsers = [...allUsers, ...this.othersAlternate];
      }

      if (this.searchResults && "other" in this.searchResults) {
        allUsers = [
          ...allUsers,
          ...this.searchResults.other.map(currUser => {
            return {
              ...currUser,
              name: currUser.otherevent_name,
              type: "other",
              phone: [],
              user_id: null
            };
          })
        ];
      }

      // Step 3: Sort and return the list
      return allUsers.sort(this.sortNames);
    },
    /**
     * List of all the chats that can be added
     */
    allChat() {
      // Step 1: Create a default list
      let allUsers = [];

      // Step 2: Add whichever list is there to the list
      if (this.filteredClients) {
        allUsers = [...this.filteredClients];
      }
      if (this.others) {
        allUsers = [...allUsers, ...this.others];
      }

      // Step 3: Sort and return the list
      return allUsers.sort(this.sortNames);
    }
  },
  methods: {
    /**
     * Escape function
     * @param {String} s
     */
    escape(s) {
      // eslint-disable-next-line no-useless-escape
      return s.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&");
    },
    /**
     * Common sort names function
     * @param {Object} a
     * @param {Object} b
     */
    sortNames(a, b) {
      if (a.name.toLowerCase() > b.name.toLowerCase()) {
        if (this.sortOrder !== false) {
          return 1;
        }
        return -1;
      } else {
        if (this.sortOrder !== false) {
          return -1;
        }
        return 1;
      }
    },

    /**
     * Checks if user has user_id
     * @param {Object} user
     */
    checkIfUserHasId(user) {
      return +user.user_id !== 0 && +user.user_id !== +this.userDetails.id;
    },
    /**
     * Check weather user can be shown or not based on favourite_mode and also filter name
     * @param {Object} user
     */
    checkNameAndFav(user) {
      // Step 1: When in favourite mode
      if (this.favouriteMode) {
        return (
          user.name
            .toLowerCase()
            .search(this.escape(this.searchBy.toLowerCase())) !== -1 &&
          user.favourite_id !== 0
        );
      }

      // Step 2: When not in favourite moode
      return (
        user.name
          .toLowerCase()
          .search(this.escape(this.searchBy.toLowerCase())) !== -1
      );
    },

    /**
     * Individual memeber check
     * @param {Object} group
     */
    individualGroupCheck(group) {
      let show = true;
      if (group.users) {
        group.users.forEach(user => {
          show = show && user.type !== "subscriber" && user.type !== "app_user";
        });
      }
      return show;
    }
  }
};
